import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useSessionTypes } from "../../hooks/useSessionTypes";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "../../components/ui/sonner";

const EMPTY = {
  photographer_id: "",
  title: "",
  description: "",
  session_type: "friendship",
  city: "",
  state: "",
  zip_code: "",
  address: "",
  session_date: "",
  session_time: "",
  duration_minutes: 90,
  price: 45,
  capacity: 6,
  image_url: "",
  what_to_expect: "",
  what_to_wear: "",
  whats_included: "",
  safety_info: "",
  cancellation_policy: "",
  stranger_booking_fee_override: "",
  photographer_booking_fee_override: "",
  booking_fee_discount: "",
  booking_fee_waived: false,
  complimentary_session: false,
};

export default function AdminSessionForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sessionTypes = useSessionTypes();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/photographers").then(({ data }) => setPhotographers(data));
  }, []);

  useEffect(() => {
    if (isEdit) {
      api.get(`/admin/sessions/${id}`).then(({ data }) => {
        setForm({
          ...data.session,
          whats_included: (data.session.whats_included || []).join(", "),
          stranger_booking_fee_override: data.session.stranger_booking_fee_override ?? "",
          photographer_booking_fee_override: data.session.photographer_booking_fee_override ?? "",
          booking_fee_discount: data.session.booking_fee_discount ?? "",
        });
        setLoading(false);
      });
    }
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.photographer_id) {
      toast.error("Please select a photographer");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      duration_minutes: Number(form.duration_minutes),
      price: Number(form.price),
      capacity: Number(form.capacity),
      whats_included: form.whats_included
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      stranger_booking_fee_override: form.stranger_booking_fee_override === "" ? null : Number(form.stranger_booking_fee_override),
      photographer_booking_fee_override: form.photographer_booking_fee_override === "" ? null : Number(form.photographer_booking_fee_override),
      booking_fee_discount: form.booking_fee_discount === "" ? null : Number(form.booking_fee_discount),
    };
    try {
      if (isEdit) {
        await api.put(`/admin/sessions/${id}`, payload);
        toast.success("Session updated");
        navigate(`/admin/sessions/${id}`);
      } else {
        const { data } = await api.post("/admin/sessions", payload);
        toast.success("Session created");
        navigate(`/admin/sessions/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="font-medium text-midnight-black/60" data-testid="admin-session-form-loading">Loading session...</p>;

  return (
    <div className="max-w-3xl" data-testid="admin-session-form-page">
      <button onClick={() => navigate("/admin/sessions")} data-testid="back-to-sessions" className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-midnight-black/60 hover:text-stranger-orange mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Sessions
      </button>
      <h2 className="font-extrabold uppercase text-2xl mb-8">{isEdit ? "Edit Session" : "Create Session"}</h2>
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-session-form">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wide text-midnight-black/50 mb-1">Photographer</label>
          <Select value={form.photographer_id} onValueChange={(v) => setForm({ ...form, photographer_id: v })}>
            <SelectTrigger data-testid="admin-session-photographer-select">
              <SelectValue placeholder="Select a photographer" />
            </SelectTrigger>
            <SelectContent>
              {photographers.map((p) => (
                <SelectItem key={p.user_id} value={p.user_id} data-testid={`photographer-option-${p.user_id}`}>
                  {p.first_name} {p.last_name} — {p.business_name || p.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Input data-testid="admin-session-title-input" placeholder="Session title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Textarea data-testid="admin-session-description-input" placeholder="Description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Select value={form.session_type} onValueChange={(v) => setForm({ ...form, session_type: v })}>
          <SelectTrigger data-testid="admin-session-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sessionTypes.map((t) => (
              <SelectItem key={t.type_key} value={t.type_key}>
                {t.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="grid grid-cols-3 gap-3">
          <Input data-testid="admin-session-city-input" placeholder="City" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <Input data-testid="admin-session-state-input" placeholder="State" required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
          <Input data-testid="admin-session-zip-input" placeholder="ZIP code" required value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
        </div>
        <Input data-testid="admin-session-address-input" placeholder="Location / address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <Input data-testid="admin-session-date-input" type="date" required value={form.session_date} onChange={(e) => setForm({ ...form, session_date: e.target.value })} />
          <Input data-testid="admin-session-time-input" type="time" required value={form.session_time} onChange={(e) => setForm({ ...form, session_time: e.target.value })} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input data-testid="admin-session-duration-input" type="number" placeholder="Duration (min)" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
          <Input data-testid="admin-session-price-input" type="number" placeholder="Price per Stranger" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <Input data-testid="admin-session-capacity-input" type="number" placeholder="Capacity" required value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
        </div>
        <Input data-testid="admin-session-image-input" placeholder="Image URL" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
        <Textarea data-testid="admin-session-what-to-expect-input" placeholder="What to expect" value={form.what_to_expect || ""} onChange={(e) => setForm({ ...form, what_to_expect: e.target.value })} />
        <Textarea data-testid="admin-session-what-to-wear-input" placeholder="What to wear" value={form.what_to_wear || ""} onChange={(e) => setForm({ ...form, what_to_wear: e.target.value })} />
        <Input data-testid="admin-session-included-input" placeholder="What's included (comma separated)" value={form.whats_included} onChange={(e) => setForm({ ...form, whats_included: e.target.value })} />
        <Textarea data-testid="admin-session-safety-input" placeholder="Safety information" value={form.safety_info || ""} onChange={(e) => setForm({ ...form, safety_info: e.target.value })} />
        <Textarea data-testid="admin-session-cancellation-input" placeholder="Cancellation policy" value={form.cancellation_policy || ""} onChange={(e) => setForm({ ...form, cancellation_policy: e.target.value })} />

        <div className="border border-soft-gray rounded-2xl p-5">
          <h2 className="font-extrabold uppercase text-sm mb-1">Platform Booking Fee Overrides</h2>
          <p className="text-xs font-medium text-midnight-black/50 mb-4">Overrides the global default booking fee for THIS session. Leave blank to use the platform default.</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Input data-testid="admin-session-stranger-fee-override-input" type="number" placeholder="Stranger fee override ($)" value={form.stranger_booking_fee_override} onChange={(e) => setForm({ ...form, stranger_booking_fee_override: e.target.value })} />
            <Input data-testid="admin-session-photographer-fee-override-input" type="number" placeholder="Photographer fee override ($)" value={form.photographer_booking_fee_override} onChange={(e) => setForm({ ...form, photographer_booking_fee_override: e.target.value })} />
          </div>
          <Input data-testid="admin-session-fee-discount-input" type="number" placeholder="Stranger fee discount amount ($, optional)" value={form.booking_fee_discount} onChange={(e) => setForm({ ...form, booking_fee_discount: e.target.value })} className="mb-4" />
          <div className="flex items-center justify-between py-2">
            <p className="text-sm font-bold">Waive Stranger Booking Fee</p>
            <Switch data-testid="admin-session-fee-waived-switch" checked={!!form.booking_fee_waived} onCheckedChange={(v) => setForm({ ...form, booking_fee_waived: v })} />
          </div>
          <div className="flex items-center justify-between py-2">
            <p className="text-sm font-bold">Complimentary Session (no fee for either party)</p>
            <Switch data-testid="admin-session-complimentary-switch" checked={!!form.complimentary_session} onCheckedChange={(v) => setForm({ ...form, complimentary_session: v })} />
          </div>
        </div>

        <Button type="submit" data-testid="admin-save-session-button" disabled={saving} className="bg-stranger-orange hover:bg-orange-600 text-clean-white font-bold rounded-full px-8 py-6">
          {saving ? "Saving..." : isEdit ? "SAVE CHANGES" : "CREATE SESSION"}
        </Button>
      </form>
    </div>
  );
}
